import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { message, Select, Spin, InputNumber, Switch } from "antd";

import ReusableHeader from "../../../../reusableField/ReusableHeader";

import {
  createRoleDetail,
  getRoleDetailById,
  updateRoleDetail,
  getRoleDetailOptions,
  getRoleDetailLocations,
  getRoleAndClientDetails,
} from "../../../../services/SyncApis";

import addphotiIcon from "../../../../Images/addphotoIcon.png";

const AddJobDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const fullState = location.state || {};

  const roleId = fullState.roleId;
  const accountId = fullState.accountId;
  const jdId = fullState.jdId;
  const editMode = fullState.editMode || false;
  const [locationOpen, setLocationOpen] = useState(false);

  /*
   * ============================================================
   * FORM DATA
   * ============================================================
   */

  const [formData, setFormData] = useState({
    companyName: "",
    jobName: fullState.prefillJobName || "",
    designation: fullState.prefillDesignation || "",

    profileImage: "",

    experienceMin: "",
    experienceMax: "",

    salaryMin: "",
    salaryMax: "",

    employmentTypeId: null,
    workModeId: null,
    locations: [],
    educationId: null,

    overview: "",
    responsibilities: "",
    requirements: "",
    benefits: "",
    isActive: true,
  });
  const [profileImageFile, setProfileImageFile] =  useState(null);
  /*
   * ============================================================
   * ORIGINAL FORM DATA
   *
   * Used only in EDIT mode to determine whether
   * anything was actually changed.
   * ============================================================
   */

  const [originalFormData, setOriginalFormData] =
    useState(null);

  /*
   * ============================================================
   * API OPTIONS
   * ============================================================
   */

  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [workModes, setWorkModes] = useState([]);
  const [educationTypes, setEducationTypes] = useState([]);

  /*
   * Location options
   */
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationCache, setLocationCache] = useState({});

  /*
   * ============================================================
   * UI STATE
   * ============================================================
   */

  const [imagePreview, setImagePreview] = useState("");

  const [errors, setErrors] = useState({});

  const [saving, setSaving] = useState(false);

  const [loadingEdit, setLoadingEdit] = useState(false);

  const [loadingOptions, setLoadingOptions] = useState(false);

  /*
   * ============================================================
   * GENERIC CHANGE HANDLER
   * ============================================================
   */

  const handleChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((previous) => {
        const updated = { ...previous };
        delete updated[field];
        return updated;
      });
    }
  };

  /*
   * ============================================================
   * IMAGE UPLOAD
   * ============================================================
   */

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setProfileImageFile(file);

    const reader = new FileReader();

    reader.onloadend = () => {
      const image = reader.result;

      setImagePreview(image);

      handleChange("profileImage", image);
    };

    reader.readAsDataURL(file);
  };

  /*
   * ============================================================
   * LOAD ROLE + CLIENT DETAILS
   *
   * Used to initialize Company Name / Role Name / Logo.
   * ============================================================
   */

  const loadRoleDetails = async () => {
    if (!accountId || !roleId) {
      return;
    }

    try {
      const response = await getRoleAndClientDetails({
        adminClientId: Number(accountId),
        adminRoleId: Number(roleId),
      });

      const data = response?.data || response;

      if (!data) return;

      setFormData((previous) => ({
        ...previous,

        companyName:
          data.clientName ||
          data.companyName ||
          previous.companyName,

        jobName:
          data.roleName ||
          previous.jobName,

        profileImage:
          data.clientLogo ||
          previous.profileImage,
      }));

      if (data.clientLogo) {
        setImagePreview(data.clientLogo);
      }
    } catch (error) {
      console.error(
        "Error loading role/client details:",
        error
      );

      message.error(
        "Failed to load role and company details."
      );
    }
  };

  /*
   * ============================================================
   * LOAD DROPDOWN OPTIONS
   * ============================================================
   */

  const loadOptions = async () => {
    setLoadingOptions(true);

    try {
      const response = await getRoleDetailOptions();

      const data = response?.data || {};

      setEmploymentTypes(
        Array.isArray(data.employmentTypes)
          ? data.employmentTypes
          : []
      );

      setWorkModes(
        Array.isArray(data.workModes)
          ? data.workModes
          : []
      );

      setEducationTypes(
        Array.isArray(data.educationTypes)
          ? data.educationTypes
          : []
      );

      if (Array.isArray(data.locations)) {
        setLocationOptions(data.locations);

        setLocationCache((previous) => {
          const updated = { ...previous };

          data.locations.forEach((location) => {
            updated[location.id] = location;
          });

          return updated;
        });
      }
    } catch (error) {
      console.error(
        "Error loading role detail options:",
        error
      );

      message.error(
        "Failed to load dropdown options."
      );
    } finally {
      setLoadingOptions(false);
    }
  };

  /*
   * ============================================================
   * LOAD LOCATIONS
   *
   * Server-side searchable locations.
   * ============================================================
   */

  const loadLocations = async (search = "") => {
    setLocationLoading(true);

    try {
      const response =
        await getRoleDetailLocations(search);

      const data = response?.data || [];

      setLocationOptions(
        Array.isArray(data) ? data : []
      );
    } catch (error) {
      console.error(
        "Error loading locations:",
        error
      );

      message.error(
        "Failed to load locations."
      );
    } finally {
      setLocationLoading(false);
    }
  };

  /*
   * ============================================================
   * INITIAL LOAD
   * ============================================================
   */

  useEffect(() => {
    loadOptions();
     if (!editMode) {
      loadRoleDetails();
    }
    loadLocations("");
  }, [roleId, accountId, editMode]);

  /*
   * ============================================================
   * LOCATION SEARCH DEBOUNCE
   * ============================================================
   */

  useEffect(() => {
    const timer = setTimeout(() => {
      loadLocations(locationSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [locationSearch]);

  /*
   * ============================================================
   * LOAD EXISTING JOB FOR EDIT
   * ============================================================
   */

  useEffect(() => {
    if (!editMode || !jdId) {
      return;
    }

    const loadJob = async () => {
      setLoadingEdit(true);

      try {
        const response =
          await getRoleDetailById(jdId);

        const jd =
          response?.data ||
          response;

        if (!jd) {
          message.error(
            "Job details not found."
          );
          return;
        }

        /*
         * Convert API response into UI state.
         */

        const existingFormData = {
          companyName:
            jd.companyName || "",

          jobName:
            jd.roleName || "",

          designation:
            jd.designation || "",

          profileImage:
            jd.jobLogo ||
            jd.profileImage ||
            "",

          experienceMin:
            jd.experienceMin ??
            "",

          experienceMax:
            jd.experienceMax ??
            "",

          salaryMin:
            jd.salaryMin ??
            "",

          salaryMax:
            jd.salaryMax ??
            "",

          employmentTypeId:
            jd.employmentTypeId ??
            null,

          workModeId:
            jd.workModeId ??
            null,

          locations:
            Array.isArray(jd.locations)
              ? jd.locations
              : [],

          educationId:
            jd.educationId ??
            null,

          overview:
            jd.overview ||
            jd.jobOverview ||
            "",

          responsibilities:
            jd.responsibilities ||
            "",

          requirements:
            jd.requirements ||
            "",

          benefits:
            jd.benefits ||
            "",

          isActive:
            jd.isActive ?? true,
        };

        setFormData(
          existingFormData
        );

        /*
         * Keep an untouched copy for comparison
         * when Save is clicked.
         */
        setOriginalFormData(
          JSON.parse(
            JSON.stringify(
              existingFormData
            )
          )
        );

        if (
          jd.jobLogo ||
          jd.profileImage
        ) {
          setImagePreview(
            jd.jobLogo ||
            jd.profileImage
          );
        }
      } catch (error) {
        console.error(
          "Error fetching job details:",
          error
        );

        message.error(
          "Failed to load job details."
        );
      } finally {
        setLoadingEdit(false);
      }
    };

    loadJob();
  }, [editMode, jdId]);

  /*
   * ============================================================
   * COMPARE VALUES
   *
   * Handles:
   * - normal values
   * - null / undefined
   * - locations array
   * ============================================================
   */

  const valuesAreEqual = (
    firstValue,
    secondValue
  ) => {
    if (
      firstValue === null ||
      firstValue === undefined
    ) {
      firstValue = "";
    }

    if (
      secondValue === null ||
      secondValue === undefined
    ) {
      secondValue = "";
    }

    /*
     * Arrays
     */
    if (
      Array.isArray(firstValue) ||
      Array.isArray(secondValue)
    ) {
      const firstArray =
        Array.isArray(firstValue)
          ? firstValue.map(Number).sort(
            (a, b) => a - b
          )
          : [];

      const secondArray =
        Array.isArray(secondValue)
          ? secondValue.map(Number).sort(
            (a, b) => a - b
          )
          : [];

      return (
        firstArray.length ===
        secondArray.length &&
        firstArray.every(
          (value, index) =>
            value ===
            secondArray[index]
        )
      );
    }

    /*
     * Numeric values
     */
    if (
      typeof firstValue === "number" ||
      typeof secondValue === "number"
    ) {
      if (
        firstValue === "" &&
        secondValue === ""
      ) {
        return true;
      }

      return (
        Number(firstValue) ===
        Number(secondValue)
      );
    }

    return (
      String(firstValue).trim() ===
      String(secondValue).trim()
    );
  };

  /*
   * ============================================================
   * GET CHANGED FIELDS
   *
   * Only fields that actually changed are sent
   * during PATCH.
   * ============================================================
   */

  const getChangedFields = () => {
    if (!originalFormData) {
      return {};
    }

    const changedFields = {};

    const fieldsToCompare = [
      "companyName",
      "jobName",
      "designation",
      "experienceMin",
      "experienceMax",
      "salaryMin",
      "salaryMax",
      "employmentTypeId",
      "workModeId",
      "locations",
      "educationId",
      "overview",
      "responsibilities",
      "requirements",
      "benefits",
      "isActive",
    ];

    fieldsToCompare.forEach(
      (field) => {
        if (
          !valuesAreEqual(
            formData[field],
            originalFormData[field]
          )
        ) {
          changedFields[field] =
            formData[field];
        }
      }
    );

    return changedFields;
  };

  /*
   * ============================================================
   * VALIDATION
   * ============================================================
   */

  const validate = () => {
    const newErrors = {};

    if (!formData.companyName?.trim()) {
      newErrors.companyName =
        "Company Name is required.";
    }

    if (!formData.jobName?.trim()) {
      newErrors.jobName =
        "Role Name is required.";
    }

    if (!formData.designation?.trim()) {
      newErrors.designation =
        "Designation is required.";
    }

    if (
      formData.experienceMin === "" ||
      formData.experienceMin === null ||
      formData.experienceMin === undefined
    ) {
      newErrors.experienceMin =
        "Minimum experience is required.";
    }

    if (
      formData.experienceMax === "" ||
      formData.experienceMax === null ||
      formData.experienceMax === undefined
    ) {
      newErrors.experienceMax =
        "Maximum experience is required.";
    }

    if (
      formData.salaryMin === "" ||
      formData.salaryMin === null ||
      formData.salaryMin === undefined
    ) {
      newErrors.salaryMin =
        "Minimum salary is required.";
    }

    if (
      formData.salaryMax === "" ||
      formData.salaryMax === null ||
      formData.salaryMax === undefined
    ) {
      newErrors.salaryMax =
        "Maximum salary is required.";
    }

    if (!formData.employmentTypeId) {
      newErrors.employmentTypeId =
        "Please select employment type.";
    }

    if (!formData.workModeId) {
      newErrors.workModeId =
        "Please select work mode.";
    }

    if (
      !Array.isArray(formData.locations) ||
      formData.locations.length === 0
    ) {
      newErrors.locations =
        "Please select at least one location.";
    }

    if (!formData.educationId) {
      newErrors.educationId =
        "Please select education.";
    }

    if (!formData.overview?.trim()) {
      newErrors.overview =
        "Job Overview is required.";
    }

    if (!formData.responsibilities?.trim()) {
      newErrors.responsibilities =
        "Responsibilities are required.";
    }

    if (!formData.requirements?.trim()) {
      newErrors.requirements =
        "Requirements are required.";
    }

    if (!formData.benefits?.trim()) {
      newErrors.benefits =
        "Benefits are required.";
    }

    /*
     * Validate min/max relationships.
     */

    if (
      formData.experienceMin !== "" &&
      formData.experienceMax !== "" &&
      Number(formData.experienceMin) >
      Number(formData.experienceMax)
    ) {
      newErrors.experienceMax =
        "Maximum experience must be greater than or equal to minimum experience.";
    }

    if (
      formData.salaryMin !== "" &&
      formData.salaryMax !== "" &&
      Number(formData.salaryMin) >
      Number(formData.salaryMax)
    ) {
      newErrors.salaryMax =
        "Maximum salary must be greater than or equal to minimum salary.";
    }

    setErrors(newErrors);

    return (
      Object.keys(newErrors).length ===
      0
    );
  };

  /*
   * ============================================================
   * ERROR CLASS
   * ============================================================
   */

  const inputErrorClass = (field) =>
    errors[field]
      ? "border-red-500"
      : "border-[#D9D9D9]";

  /*
   * ============================================================
   * SAVE
   * ============================================================
   */

  const handleSave = async () => {
    if (!validate()) {
      message.warning(
        "Fill all the mandatory fields."
      );
      return;
    }

    if (!roleId) {
      message.error(
        "Role ID is missing. Cannot save."
      );
      return;
    }

    /*
     * ========================================================
     * EDIT MODE
     *
     * First determine whether anything changed.
     * ========================================================
     */

    if (editMode && jdId) {
      const changedFields =
        getChangedFields();

      /*
       * Nothing changed.
       *
       * Do NOT call PATCH.
       */

      const imageChanged = profileImageFile instanceof File;

      if (
        Object.keys(changedFields)
          .length === 0  && !imageChanged
      ) {
        message.success(
          "No changes were made."
        );

        navigate(-1);
        return;
      }
    }

    setSaving(true);

    try {
      /*
       * ========================================================
       * CREATE PAYLOAD
       * ========================================================
       */

      const payload = {
        companyName: formData.companyName.trim(),
        roleName: formData.jobName.trim(),
        designation:
          formData.designation.trim(),

        salaryMin:
          Number(formData.salaryMin),

        salaryMax:
          Number(formData.salaryMax),

        experienceMin:
          Number(formData.experienceMin),

        experienceMax:
          Number(formData.experienceMax),

        employmentTypeId:
          Number(
            formData.employmentTypeId
          ),

        workModeId:
          Number(formData.workModeId),

        locations:
          formData.locations.map(
            (location) =>
              Number(location)
          ),

        educationId:
          Number(formData.educationId),

        overview:
          formData.overview.trim(),

        responsibilities:
          formData.responsibilities.trim(),

        requirements:
          formData.requirements.trim(),

        benefits:
          formData.benefits.trim(),

        isActive:
          Boolean(formData.isActive),
      };

      /*
       * ========================================================
       * OPTIONS
       * ========================================================
       */

      const options = {};

      if (accountId) {
        options.adminClientId =
          Number(accountId);
      }

      if (roleId) {
        options.adminRoleId =
          Number(roleId);
      }

      /*
       * Keep changedBy exactly as it currently works.
       */

      if (
        fullState.changedBy !==
        undefined &&
        fullState.changedBy !== null
      ) {
        options.changedBy =
          Number(fullState.changedBy);
      }

      /*
       * ========================================================
       * UPDATE
       * ========================================================
       */

      if (editMode && jdId) {
        const changedFields =
          getChangedFields();

        /*
         * Convert only the fields that are
         * actually changed into the PATCH payload.
         *
         * Do not send UI-only fields.
         */

        const patchPayload = {};

        /*
         * Editable text fields
         */

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "designation"
          )
        ) {
          patchPayload.designation =
            changedFields.designation?.trim();
        }

        /*
         * Numeric fields
         */

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "salaryMin"
          )
        ) {
          patchPayload.salaryMin =
            Number(
              changedFields.salaryMin
            );
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "salaryMax"
          )
        ) {
          patchPayload.salaryMax =
            Number(
              changedFields.salaryMax
            );
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "experienceMin"
          )
        ) {
          patchPayload.experienceMin =
            Number(
              changedFields.experienceMin
            );
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "experienceMax"
          )
        ) {
          patchPayload.experienceMax =
            Number(
              changedFields.experienceMax
            );
        }

        /*
         * Dropdowns
         */

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "employmentTypeId"
          )
        ) {
          patchPayload.employmentTypeId =
            Number(
              changedFields.employmentTypeId
            );
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "workModeId"
          )
        ) {
          patchPayload.workModeId =
            Number(
              changedFields.workModeId
            );
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "educationId"
          )
        ) {
          patchPayload.educationId =
            Number(
              changedFields.educationId
            );
        }

        /*
         * Location
         */

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "locations"
          )
        ) {
          patchPayload.locations =
            changedFields.locations.map(
              (location) =>
                Number(location)
            );
        }

        /*
         * Description fields
         */

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "overview"
          )
        ) {
          patchPayload.overview =
            changedFields.overview?.trim();
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "responsibilities"
          )
        ) {
          patchPayload.responsibilities =
            changedFields.responsibilities?.trim();
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "requirements"
          )
        ) {
          patchPayload.requirements =
            changedFields.requirements?.trim();
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "benefits"
          )
        ) {
          patchPayload.benefits =
            changedFields.benefits?.trim();
        }

        /*
         * Company name / role name / profile image
         *
         * These are editable in the UI.
         */

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "companyName"
          )
        ) {
          patchPayload.companyName =
            changedFields.companyName?.trim();
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "jobName"
          )
        ) {
          patchPayload.roleName =
            changedFields.jobName?.trim();
        }

        if (
          Object.prototype.hasOwnProperty.call(
            changedFields,
            "isActive"
          )
        ) {
          patchPayload.isActive =
            Boolean(changedFields.isActive);
        }

        console.log(
          "CHANGED FIELDS:",
          changedFields
        );

        console.log(
          "FINAL PATCH PAYLOAD:",
          patchPayload
        );

        console.log(
          "PATCH OPTIONS:",
          options
        );

        await updateRoleDetail(
          jdId,
          patchPayload,
          options,
          profileImageFile
        );

        message.success(
          "Job Details Updated Successfully"
        );
      } else {
        /*
         * ======================================================
         * CREATE
         * ======================================================
         */

        await createRoleDetail(
          payload,
          options,
          profileImageFile
        );

        message.success(
          "Job Details Saved Successfully"
        );
      }

      navigate(-1);
    } catch (error) {
      console.error(
        "Error saving job details:",
        error
      );

      /*
       * Show backend field-level validation errors
       * directly under their respective fields.
       */
      const backendErrors =
        error?.response?.data?.errors;

      if (
        backendErrors &&
        typeof backendErrors === "object"
      ) {
        setErrors((previous) => ({
          ...previous,
          ...backendErrors,
        }));
      }

      const apiMessage =
        error?.response?.data?.message;

      message.error(
        apiMessage ||
        "Failed to save job details. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div className="min-h-screen bg-[#F6F6F6]">
      <ReusableHeader />

      <div className="px-8 pb-8 -mt-6">

        {/* ======================================================
            BACK
        ====================================================== */}

        <button
          onClick={() => navigate(-1)}
          className="
            flex
            items-center
            gap-2
            text-[#AD0101]
            text-sm
            font-medium
            ml-[-12px]
          "
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="mt-5 flex items-center justify-between">
          <p
            className="
              text-[16px]
              leading-[20px]
              font-[600]
              text-[#292929]
            "
          >
            Define job and role details for this configuration.
          </p>

          {editMode && (
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isActive}
                onChange={(checked) =>
                  handleChange("isActive", checked)
                }
                size="small"
              />

              <span className="text-[14px] text-[#292929]">
                {formData.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          )}
        </div>

        {loadingEdit ? (
          <div className="flex justify-center items-center h-[300px]">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* ==================================================
                JOB BASIC DETAILS
            ================================================== */}

            <div
              className="
                bg-white
                rounded-xl
                shadow-sm
                mt-4
                p-6
                border
                border-[#E5E5E5]
              "
            >
              <h3 className="text-[16px] font-semibold mb-5">
                Job Basic Details
              </h3>

              <div className="flex items-start gap-6">

                {/* ===============================
                    IMAGE
                =============================== */}

                <div
                  className="
                    relative
                    w-[140px]
                    h-[140px]
                    shrink-0
                    cursor-pointer
                  "
                onClick={() =>
                  fileInputRef.current?.click()
                }
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Company"
                      className="
                        w-[140px]
                        h-[140px]
                        rounded-full
                        object-cover
                      "
                    />
                  ) : (
                    <div
                      className="
                        w-[140px]
                        h-[140px]
                        rounded-full
                        bg-[#2F5D8C]
                        flex
                        items-center
                        justify-center
                        text-white
                        text-[48px]
                        font-semibold
                      "
                    >
                      {(
                        formData.companyName ||
                        formData.jobName ||
                        "G"
                      )
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                  )}

                  {/* CAMERA */}

                  <div
                    className="
                      absolute
                      bottom-[5px]
                      right-[-2px]
                      w-[34px]
                      h-[34px]
                      rounded-full
                      bg-white
                      border
                      border-[#D9DDE3]
                      flex
                      items-center
                      justify-center
                      shadow-sm
                    "
                  >
                    <img
                      src={addphotiIcon}
                      alt="Add photo"
                      className="
                        w-[40px]
                        h-[40px]
                        object-contain
                      "
                    />
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>

                {/* ===============================
                    BASIC FIELDS
                =============================== */}

                <div className="flex-1 grid grid-cols-2 gap-x-8 gap-y-4">

                  {/* COMPANY NAME */}

                  <div>
                    <label className="block mb-1 text-[14px] font-medium">
                      Company Name{" "}
                      <span className="text-red-600">
                        *
                      </span>
                    </label>

                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) =>
                        handleChange(
                          "companyName",
                          e.target.value
                        )
                      }
                      placeholder="Enter Company Name"
                      className={`
                        w-full
                        h-[40px]
                        rounded-[6px]
                        px-3
                        text-[14px]
                        border
                        outline-none
                        ${inputErrorClass(
                        "companyName"
                      )}
                      `}
                    />

                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.companyName}
                      </p>
                    )}
                  </div>

                  {/* ROLE NAME */}

                  <div>
                    <label className="block mb-1 text-[14px] font-medium">
                      Role Name{" "}
                      <span className="text-red-600">
                        *
                      </span>
                    </label>

                    <input
                      type="text"
                      value={formData.jobName}
                      onChange={(e) =>
                        handleChange(
                          "jobName",
                          e.target.value
                        )
                      }
                      placeholder="Enter Job Name"
                      className={`
                        w-full
                        h-[40px]
                        rounded-[6px]
                        px-3
                        text-[14px]
                        border
                        outline-none
                        ${inputErrorClass(
                        "jobName"
                      )}
                      `}
                    />

                    {errors.jobName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.jobName}
                      </p>
                    )}
                  </div>

                  {/* DESIGNATION */}

                  <div>
                    <label className="block mb-1 text-[14px] font-medium">
                      Designation
                      <span className="text-red-600">
                        *
                      </span>
                    </label>

                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) =>
                        handleChange(
                          "designation",
                          e.target.value
                        )
                      }
                      placeholder="Enter Designation"
                      className={`
                        w-full
                        h-[40px]
                        rounded-[6px]
                        px-3
                        text-[14px]
                        border
                        outline-none
                        ${inputErrorClass(
                        "designation"
                      )}
                      `}
                    />

                    {errors.designation && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.designation}
                      </p>
                    )}
                  </div>

                </div>
              </div>
            </div>

            {/* ==================================================
                ROLE REQUIREMENTS
            ================================================== */}

            <div
              className="
                bg-white
                rounded-xl
                shadow-sm
                mt-5
                p-6
                border
                border-[#E5E5E5]
              "
            >
              <h3 className="text-[16px] font-semibold mb-5">
                Role Requirements
              </h3>

              <div className="grid grid-cols-2 gap-x-6 gap-y-3">

                {/* EXPERIENCE MIN */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Experience (Min){" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                 <InputNumber
  min={0}
  value={formData.experienceMin}
  onChange={(value) => handleChange("experienceMin", value)}
  placeholder="Enter Experience"
  controls
  className={`
    w-full
    h-[35px]
    rounded-[6px]
    text-[14px]
    flex
    items-center
    [&_.ant-input-number-input]:h-[33px]
    [&_.ant-input-number-input]:flex
    [&_.ant-input-number-input]:items-center
    [&_.ant-input-number-input]:text-[14px]
    [&_.ant-input-number-handler-wrap]:rounded-r-[6px]
    [&_.ant-input-number-handler-wrap]:opacity-100
    ${inputErrorClass("experienceMin")}
  `}
/>

                  {errors.experienceMin && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.experienceMin}
                    </p>
                  )}
                </div>

                {/* EXPERIENCE MAX */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Experience (Max){" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                 <InputNumber
  min={0}
  value={formData.experienceMax}
  onChange={(value) => handleChange("experienceMax", value)}
  placeholder="Enter Experience"
  controls
  className={`
    w-full h-[35px] rounded-[6px] text-[14px] flex items-center
    [&_.ant-input-number-input]:h-[33px]
    [&_.ant-input-number-input]:flex
    [&_.ant-input-number-input]:items-center
    [&_.ant-input-number-input]:text-[14px]
    [&_.ant-input-number-handler-wrap]:rounded-r-[6px]
    [&_.ant-input-number-handler-wrap]:opacity-100
    ${inputErrorClass("experienceMax")}
  `}
/>

                  {errors.experienceMax && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.experienceMax}
                    </p>
                  )}
                </div>

                {/* SALARY MIN */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Salary (Min){" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                 <InputNumber
  min={0}
  value={formData.salaryMin}
  onChange={(value) => handleChange("salaryMin", value)}
  placeholder="Enter Salary"
  controls
  className={`
    w-full h-[35px] rounded-[6px] text-[14px] flex items-center
    [&_.ant-input-number-input]:h-[33px]
    [&_.ant-input-number-input]:flex
    [&_.ant-input-number-input]:items-center
    [&_.ant-input-number-input]:text-[14px]
    [&_.ant-input-number-handler-wrap]:rounded-r-[6px]
    [&_.ant-input-number-handler-wrap]:opacity-100
    ${inputErrorClass("salaryMin")}
  `}
/>

                  {errors.salaryMin && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.salaryMin}
                    </p>
                  )}
                </div>

                {/* SALARY MAX */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Salary (Max){" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                 <InputNumber
  min={0}
  value={formData.salaryMax}
  onChange={(value) => handleChange("salaryMax", value)}
  placeholder="Enter Salary"
  controls
  className={`
    w-full h-[35px] rounded-[6px] text-[14px] flex items-center
    [&_.ant-input-number-input]:h-[33px]
    [&_.ant-input-number-input]:flex
    [&_.ant-input-number-input]:items-center
    [&_.ant-input-number-input]:text-[14px]
    [&_.ant-input-number-handler-wrap]:rounded-r-[6px]
    [&_.ant-input-number-handler-wrap]:opacity-100
    ${inputErrorClass("salaryMax")}
  `}
/>

                  {errors.salaryMax && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.salaryMax}
                    </p>
                  )}
                </div>

                {/* EMPLOYMENT TYPE */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Employment Type{" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                  <Select

                    value={
                      formData.employmentTypeId
                    }
                    loading={loadingOptions}
                    placeholder="Select Employment Type"
                    className={`
                      w-full
                      ${inputErrorClass(
                      "employmentTypeId"
                    )}
                    `}
                    optionFilterProp="label"
                    filterOption={(
                      input,
                      option
                    ) =>
                      (
                        option?.label || ""
                      )
                        .toLowerCase()
                        .includes(
                          input.toLowerCase()
                        )
                    }
                    options={employmentTypes.map(
                      (type) => ({
                        value: type.id,
                        label: type.name,
                      })
                    )}
                    onChange={(value) =>
                      handleChange(
                        "employmentTypeId",
                        value
                      )
                    }
                  />

                  {errors.employmentTypeId && (
                    <p className="text-red-500 text-sm mt-1">
                      {
                        errors.employmentTypeId
                      }
                    </p>
                  )}
                </div>

                {/* WORK MODE */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Work Mode{" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                  <Select
                    // searchable

                    value={
                      formData.workModeId
                    }
                    loading={loadingOptions}
                    placeholder="Select Work Mode"
                    className={`
                      w-full
                      ${inputErrorClass(
                      "workModeId"
                    )}
                    `}
                    optionFilterProp="label"
                    filterOption={(
                      input,
                      option
                    ) =>
                      (
                        option?.label || ""
                      )
                        .toLowerCase()
                        .includes(
                          input.toLowerCase()
                        )
                    }
                    options={workModes.map(
                      (mode) => ({
                        value: mode.id,
                        label: mode.name,
                      })
                    )}
                    onChange={(value) =>
                      handleChange(
                        "workModeId",
                        value
                      )
                    }
                  />

                  {errors.workModeId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.workModeId}
                    </p>
                  )}
                </div>

                {/* LOCATION */}

                <div>
  <label className="block mb-1 text-[14px] font-medium">
    Location
    <span className="text-red-600">*</span>
  </label>

  <div className="relative">
    <Select
      mode="multiple"
      showSearch
      value={formData.locations}
      placeholder=""
      loading={locationLoading}
      className={`w-full custom-select ${inputErrorClass("locations")}`}
      optionFilterProp="label"
      filterOption={false}
      tagRender={() => null}
      onOpenChange={(open) => {
        setLocationOpen(open);
        if (!open) setLocationSearch("");
      }}
      onSearch={(value) => setLocationSearch(value)}
      onChange={(values) => handleChange("locations", values)}
      options={[
        ...formData.locations
          .map((locationId) => locationCache[locationId])
          .filter(Boolean),
        ...locationOptions,
      ]
        .filter(
          (location, index, array) =>
            array.findIndex((item) => Number(item.id) === Number(location.id)) === index
        )
        .map((location) => ({
          value: location.id,
          label: location.name,
        }))}
      notFoundContent={locationLoading ? <Spin size="small" /> : "No locations found"}
    />

    {!locationOpen && (
      <span
        className="
          pointer-events-none
          absolute
          left-[11px]
          top-1/2
          -translate-y-1/2
          text-[#BFBFBF]
          text-[14px]
        "
      >
        {formData.locations.length > 0
          ? `${formData.locations.length} location${formData.locations.length > 1 ? "s" : ""} selected`
          : "Select Location"}
      </span>
    )}
  </div>

  {errors.locations && (
    <p className="text-red-500 text-sm mt-1">{errors.locations}</p>
  )}
</div>

                {/* EDUCATION */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Education{" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                  <Select
                    
                    value={
                      formData.educationId
                    }
                    loading={loadingOptions}
                    placeholder="Select Education"
                    className="
                      w-full
                      custom-select
                    "
                    // optionFilterProp="label"
                    // filterOption={(
                    //   input,
                    //   option
                    // ) =>
                    //   (
                    //     option?.label || ""
                    //   )
                    //     .toLowerCase()
                    //     .includes(
                    //       input.toLowerCase()
                    //     )
                    // }
                    options={educationTypes.map(
                      (education) => ({
                        value: education.id,
                        label: education.name,
                      })
                    )}
                    onChange={(value) =>
                      handleChange(
                        "educationId",
                        value
                      )
                    }
                  />

                  {errors.educationId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.educationId}
                    </p>
                  )}
                </div>
              </div>

              {/* ==================================================
                  LOCATION PREVIEW
              ================================================== */}

              {formData.locations.length >
                0 && (
                  <div className="mt-5 w-full">
                    <label className="block mb-2 text-[14px] font-medium">
                      Location Preview
                    </label>

                    <div
                      className="
                      w-full
                      min-h-[58px]
                      border
                      border-[#D9D9D9]
                      rounded-[6px]
                      px-3
                      py-2
                      flex
                      flex-wrap
                      items-center
                      gap-2
                      bg-white
                    "
                    >
                      {formData.locations.map(
                        (locationId) => {
                          const location =
                            locationOptions.find(
                              (item) =>
                                Number(item.id) ===
                                Number(
                                  locationId
                                )
                            );

                          return (
                            <span
                              key={locationId}
                              className="
                              inline-flex
                              items-center
                              gap-1
                              bg-[#AD0101]
                              text-white
                              rounded-[4px]
                              px-2
                              py-[4px]
                              text-[12px]
                              font-medium
                            "
                            >
                              {location?.name ||
                                locationId}

                              <button
                                type="button"
                                onClick={() => {
                                  handleChange(
                                    "locations",
                                    formData.locations.filter(
                                      (id) =>
                                        Number(
                                          id
                                        ) !==
                                        Number(
                                          locationId
                                        )
                                    )
                                  );
                                }}
                                className="
                                flex
                                items-center
                                justify-center
                                hover:opacity-80
                              "
                              >
                                <X
                                  size={13}
                                  strokeWidth={
                                    2.5
                                  }
                                />
                              </button>
                            </span>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* ==================================================
                JOB DESCRIPTION
            ================================================== */}

            <div
              className="
                bg-white
                rounded-xl
                shadow-sm
                mt-5
                p-6
                border
                border-[#E5E5E5]
              "
            >
              <h3 className="text-[16px] font-semibold mb-5">
                Job Description
              </h3>

              <div className="space-y-5">

                {/* OVERVIEW */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Job Overview
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                  <textarea
                    value={formData.overview}
                    onChange={(e) =>
                      handleChange(
                        "overview",
                        e.target.value
                      )
                    }
                    rows={4}
                    className={`
                      w-full
                      rounded-md
                      px-3
                      py-2
                      text-[14px]
                      border
                      outline-none
                      resize-none
                      ${inputErrorClass(
                      "overview"
                    )}
                    `}
                  />

                  {errors.overview && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.overview}
                    </p>
                  )}
                </div>

                {/* RESPONSIBILITIES */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Responsibilities{" "}
                    <span className="text-red-600 ml-0">
                      *
                    </span>
                  </label>

                  <textarea
                    value={
                      formData.responsibilities
                    }
                    onChange={(e) =>
                      handleChange(
                        "responsibilities",
                        e.target.value
                      )
                    }
                    rows={4}
                    className={`
                      w-full
                      rounded-md
                      px-3
                      py-2
                      text-[14px]
                      border
                      outline-none
                      resize-none
                      ${inputErrorClass(
                      "responsibilities"
                    )}
                    `}
                  />

                  {errors.responsibilities && (
                    <p className="text-red-500 text-sm mt-1">
                      {
                        errors.responsibilities
                      }
                    </p>
                  )}
                </div>

                {/* REQUIREMENTS */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Requirements{" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                  <textarea
                    value={
                      formData.requirements
                    }
                    onChange={(e) =>
                      handleChange(
                        "requirements",
                        e.target.value
                      )
                    }
                    rows={4}
                    className={`
                      w-full
                      rounded-md
                      px-3
                      py-2
                      text-[14px]
                      border
                      outline-none
                      resize-none
                      ${inputErrorClass(
                      "requirements"
                    )}
                    `}
                  />

                  {errors.requirements && (
                    <p className="text-red-500 text-sm mt-1">
                      {
                        errors.requirements
                      }
                    </p>
                  )}
                </div>

                {/* BENEFITS */}

                <div>
                  <label className="block mb-1 text-[14px] font-medium">
                    Benefits{" "}
                    <span className="text-red-600">
                      *
                    </span>
                  </label>

                  <textarea
                    value={formData.benefits}
                    onChange={(e) =>
                      handleChange(
                        "benefits",
                        e.target.value
                      )
                    }
                    rows={4}
                    className={`
                      w-full
                      rounded-md
                      px-3
                      py-2
                      text-[14px]
                      border
                      outline-none
                      resize-none
                      ${inputErrorClass(
                      "benefits"
                    )}
                    `}
                  />

                  {errors.benefits && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.benefits}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ==================================================
                FOOTER
            ================================================== */}

            <div className="flex justify-end gap-3 mt-6">

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="
                  border
                  border-gray-300
                  bg-white
                  px-6
                  h-10
                  rounded-[6px]
                  text-[14px]
                "
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="
                  bg-[#AD0101]
                  text-white
                  px-7
                  h-10
                  rounded-[6px]
                  text-[14px]
                  disabled:opacity-50
                "
              >
                {saving
                  ? "Saving..."
                  : editMode
                    ? "Update"
                    : "Save"}
              </button>

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddJobDetails;